/**
 * Best-effort, human-readable descriptor of the sub-state the reviewer is
 * looking at — the open dialog/drawer title, the active tab, a wizard step, or
 * the page heading. A single route can host many of these (a multi-step wizard,
 * a modal, tabs), which the URL doesn't distinguish; this keeps comments on the
 * same route apart. Reads the LIVE DOM, skipping the overlay's own portal
 * (`.rcmt-portal`) so it never describes itself. The reviewer can edit the
 * result, so this only has to be a good first guess.
 */

const MAX = 200;

function isOurs(el: Element | null | undefined): boolean {
  return !!el && !!el.closest(".rcmt-portal");
}

function text(el: Element | null | undefined): string {
  return (el?.textContent ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Resolve an `aria-labelledby` attribute to its accessible name. ARIA permits a
 * space-separated id list; concatenate each referenced element's text with a
 * single space (ARIA accessible-name computation), instead of assuming one id
 * (a multi-id value would return null from a single getElementById).
 */
function labelledByText(labelledBy: string | null): string {
  if (!labelledBy) return "";
  return labelledBy
    .split(/\s+/)
    .filter(Boolean)
    .map((id) => text(document.getElementById(id)))
    .filter(Boolean)
    .join(" ")
    .trim();
}

function truncate(s: string): string {
  // Append an ellipsis when sliced so a cut descriptor is visibly distinct from
  // a complete one; keep the total within MAX (the DB column is String(300)).
  return s.length <= MAX ? s : s.slice(0, MAX - 1).trimEnd() + "…";
}

export function deriveContext(): string {
  // 1. Top-most open app dialog/drawer — its accessible title.
  const dialogs = Array.from(
    document.querySelectorAll<HTMLElement>('[role="dialog"],[role="alertdialog"]'),
  ).filter((d) => !isOurs(d));
  if (dialogs.length) {
    const d = dialogs[dialogs.length - 1]!; // last in DOM ≈ top-most
    const labelledBy = d.getAttribute("aria-labelledby");
    const title =
      labelledByText(labelledBy) ||
      d.getAttribute("aria-label") ||
      text(d.querySelector("h1,h2,h3"));
    if (title) return truncate(title);
  }

  // 2. Active tab (router TabStrip or Radix Tabs).
  const tab = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[role="tab"][aria-selected="true"],[role="tab"][data-state="active"]',
    ),
  ).find((t) => !isOurs(t));
  if (tab && text(tab)) return truncate(text(tab));

  // 3. A wizard step indicator. Prefer a SHORT step heading (e.g. "Step 1:
  //    Assign Deal Owner") over a big stepper <nav> that lists every step; fall
  //    back to just "Step N" extracted from whatever matched.
  const stepNodes = Array.from(
    document.querySelectorAll<HTMLElement>("h1,h2,h3,[aria-current],[aria-label],nav"),
  ).filter((e) => !isOurs(e) && /\bstep\s+\d+\b/i.test(text(e)));
  const shortStep = stepNodes.find((e) => text(e).length <= 70);
  if (shortStep) return truncate(text(shortStep));
  if (stepNodes.length) {
    const m = text(stepNodes[0]!).match(/step\s+\d+(\s+of\s+\d+)?/i);
    if (m) return truncate(m[0]);
  }

  // 4. Page heading.
  const h1 = Array.from(document.querySelectorAll<HTMLElement>("h1")).find((h) => !isOurs(h));
  if (h1 && text(h1)) return truncate(text(h1));

  return "";
}
